/**
 * MechanicLeaderboard — ranked-row mechanic.
 *
 * Drives 24h leaderboards, timed competitions, seasonal climbs. Renders
 * the top-N rows + the user's row (sticky-highlighted) so personal stake
 * is always visible even when you're #47.
 *
 * Slot variants:
 *   - marquee: top-3 + your row in a red-tinted highlight
 *   - tile:    a single "YOU #47" red chip + type label
 *   - browse:  top-3 + your row, slightly tighter spacing
 */

import React from "react";
import "./MechanicLeaderboard.scss";

export interface LeaderboardRow {
    /**
     * Who this row belongs to — a player/user id. Required because it is the
     * ONLY safe way to tell whether a row on the board is the viewer's own.
     * Rank can't do it: ranks tie, and when the viewer ties with someone in the
     * top window, matching on the number highlighted the OTHER player's row as
     * "you" and dropped the viewer's row from the board entirely.
     */
    id: string;
    rank: number;
    name: string;
    score: number;
}

interface MechanicLeaderboardProps {
    variant: "marquee" | "tile" | "browse";
    /** Top-of-board rows. Component renders the first `topN` (default 3 marquee, 3 browse). */
    rows: LeaderboardRow[];
    /** The user's own row — when missing the user isn't on the board yet. */
    yourRow?: LeaderboardRow;
    /** Override how many of `rows` to surface. */
    topN?: number;
    /** Optional copy under the table (e.g. number of players, time-decay note). */
    note?: string;
}

function formatScore(n: number): string {
    return n.toLocaleString("en-US");
}

export const MechanicLeaderboard: React.FC<MechanicLeaderboardProps> = ({
    variant, rows, yourRow, topN, note,
}) => {
    if (variant === "tile") {
        return (
            <span className="mechanic-lb mechanic-lb--tile" role="img" aria-label={yourRow ? `You are ranked ${yourRow.rank}` : "Not yet ranked"}>
                {yourRow
                    ? <span className="mechanic-lb__tile-chip">YOU #{yourRow.rank}</span>
                    : <span className="mechanic-lb__tile-chip mechanic-lb__tile-chip--join">JOIN</span>
                }
            </span>
        );
    }

    const limit = topN ?? 3;
    const top = rows.slice(0, limit);

    /*
     * If the user's row isn't in the top window, show a "…" spacer row above
     * their entry so the gap is visible. Matched on identity, never on rank —
     * see the note on LeaderboardRow.id.
     */
    const userOutsideTop = yourRow && !top.some(r => r.id === yourRow.id);

    return (
        <div className={`mechanic-lb mechanic-lb--${variant}`}>
            <ol className="mechanic-lb__rows" aria-label="Leaderboard">
                {top.map(row =>
                    <li
                        key={`top-${row.id}`}
                        className={`mechanic-lb__row${yourRow && row.id === yourRow.id ? " is-you" : ""}`}
                    >
                        <span className="mechanic-lb__rank">#{row.rank}</span>
                        <span className="mechanic-lb__name">{row.name}</span>
                        <span className="mechanic-lb__score">{formatScore(row.score)}</span>
                    </li>)}

                {userOutsideTop && yourRow &&
                    <>
                        <li className="mechanic-lb__row mechanic-lb__row--spacer" aria-hidden="true">···</li>
                        <li className="mechanic-lb__row is-you">
                            <span className="mechanic-lb__rank">#{yourRow.rank}</span>
                            <span className="mechanic-lb__name">{yourRow.name}</span>
                            <span className="mechanic-lb__score">{formatScore(yourRow.score)}</span>
                        </li>
                    </>
                }
            </ol>

            {note && variant === "marquee" &&
                <div className="mechanic-lb__note">{note}</div>
            }
        </div>
    );
};
